import React from 'react';
import { Typography, Spin } from 'antd';
import dayjs from 'dayjs';
import { DATE_FORMAT_SENTENCE } from 'components/AriaDatePicker/AriaDatePicker';
import { RevealingLink } from 'components/ui/RevealingLink/RevealingLink';

const { Text } = Typography;

const STATES = {
  HIDE: 'HIDE',
  DEACTIVATED: 'DEACTIVATED',
  NOT_STARTED: 'NOT_STARTED',
  PROGRAM_DETAILS: 'PROGRAM_DETAILS',
  COUNTDOWN: 'COUNTDOWN',
  REVIEWING_DOCS: 'REVIEWING_DOCS',
  REVIEWING_APP: 'REVIEWING_APP',
};

function pickState(s) {
  if (s.applicationApproved && s.programAcknowledged) return STATES.HIDE;
  if (s.daysUntilDeactivation <= 0) return STATES.DEACTIVATED;
  if (s.daysUntilDeactivation > 180) return STATES.NOT_STARTED;
  if (!s.programAcknowledged) return STATES.PROGRAM_DETAILS;
  if (s.requestedCount > 0) return STATES.COUNTDOWN;
  if (s.submittedCount > 0) return STATES.REVIEWING_DOCS;
  if (s.totalCount > 0) return STATES.REVIEWING_DOCS;
  return STATES.REVIEWING_APP;
}

function getBannerStyle(state, daysUntilDeactivation) {
  if (state === STATES.DEACTIVATED || (state === STATES.COUNTDOWN && daysUntilDeactivation >= 0)) {
    if (daysUntilDeactivation <= 15) return 'border-l-red-500 bg-red-50';
    if (daysUntilDeactivation <= 30) return 'border-l-yellow-400 bg-yellow-50';
  }
  return 'border-l-[#4654fc] bg-[#f0f5ff]';
}

export default function StatusBanner({ statusData, loading }) {
  if (loading) {
    return (
      <div className="flex justify-center py-6">
        <Spin />
      </div>
    );
  }

  if (!statusData) return null;
  if (!statusData.checklistActive) return null;

  const {
    submittedDate,
    appStartDate,
    deactivationDeadline,
    daysUntilDeactivation,
    requestedCount,
    programAcknowledged,
  } = statusData;

  const state = pickState(statusData);

  if (state === STATES.HIDE) return null;

  const hasOutstandingRequests = (requestedCount ?? 0) > 0;

  const fmt = (d) => (d ? dayjs(d).format(DATE_FORMAT_SENTENCE) : null);
  const fmtSubmitted = fmt(submittedDate);
  const fmtAppStart = fmt(appStartDate);
  const fmtDeadline = fmt(deactivationDeadline);
  const fmtReturnDate = appStartDate ? dayjs(appStartDate).subtract(90, 'day').format(DATE_FORMAT_SENTENCE) : null;

  const bannerStyle = getBannerStyle(state, daysUntilDeactivation);
  const wrapperClass = `p-6 rounded-lg border-l-4 border-solid ${bannerStyle}`;

  if (state === STATES.PROGRAM_DETAILS) {
    return (
      <div className={wrapperClass}>
        <Text className="block font-bold">
          Your application has been submitted{fmtSubmitted ? ` on ${fmtSubmitted}` : ''}
        </Text>
        {!programAcknowledged && (
          <Text className="block text-gray-700 mt-2">Start with Program details — about 30 seconds.</Text>
        )}
      </div>
    );
  }

  if (state === STATES.REVIEWING_APP) {
    return (
      <div className={wrapperClass}>
        <Text className="block font-bold">
          Your application has been submitted{fmtSubmitted ? ` on ${fmtSubmitted}` : ''}
        </Text>
        <Text className="block text-gray-700 mt-2">
          {!programAcknowledged
            ? 'Start with Program details — about 30 seconds.'
            : 'Your coordinator is reviewing your application.'}
        </Text>
      </div>
    );
  }

  if (state === STATES.COUNTDOWN) {
    return (
      <div className={wrapperClass}>
        {fmtAppStart && <Text className="block text-gray-500 text-sm mb-1">Earliest Eligible Date: {fmtAppStart}</Text>}

        <Text className="block font-bold text-base mt-4">Required by {fmtDeadline}</Text>
        <Text className="mt-1">
          Please review each item carefully and complete any requests, which may include uploading a document, providing
          information, or reviewing messages.
        </Text>

        {!programAcknowledged && (
          <Text className="block text-gray-700 mt-3">Start with Program details — about 30 seconds.</Text>
        )}

        <RevealingLink
          text="Show details"
          expandedText="Hide details"
          className="mt-4"
          reveals={
            <Text className="block text-gray-700">
              As of today, <span className="font-bold">{dayjs().format(DATE_FORMAT_SENTENCE)}</span>, you have{' '}
              <span className="font-bold">{daysUntilDeactivation} days</span> to submit all requested documents required
              by the Fee Assistance Program to Child Care Aware® of America to determine eligibility.
            </Text>
          }
        />
      </div>
    );
  }

  if (state === STATES.REVIEWING_DOCS) {
    return (
      <div className={wrapperClass}>
        {fmtAppStart && <Text className="block text-gray-500 text-sm mb-1">Earliest Eligible Date {fmtAppStart}</Text>}

        <Text className="block font-bold mt-2">Your coordinator is reviewing your documents</Text>

        {!programAcknowledged ? (
          <Text className="block text-gray-700 mt-3">Start with Program details — about 30 seconds.</Text>
        ) : (
          <Text className="block text-gray-700 mt-2">
            No action needed right now. We&apos;ll reach out if we need anything from you
          </Text>
        )}
      </div>
    );
  }

  if (state === STATES.DEACTIVATED) {
    const headerParts = [
      fmtSubmitted ? `Submitted ${fmtSubmitted}` : null,
      fmtDeadline ? `Deactivated ${fmtDeadline}` : null,
    ].filter(Boolean);

    return (
      <div className={wrapperClass}>
        {headerParts.length > 0 && <Text className="block text-gray-500 text-sm mb-1">{headerParts.join(' · ')}</Text>}

        <Text className="block font-bold text-base mt-3">Application deactivated</Text>

        <Text className="block text-gray-700 mt-3">
          Your documents were <span className="font-bold">not submitted within 90 days</span> of your initial
          application submission. You can still submit the required documents to continue the process. If approved, your{' '}
          <span className="font-bold">future</span> eligibility for fee assistance will{' '}
          <span className="font-bold">start on the date your documents are first received.</span>
        </Text>

        {hasOutstandingRequests && (
          <Text className="block text-gray-700 mt-3">
            Review each item below and complete any outstanding requests to move forward.
          </Text>
        )}

        {!programAcknowledged && (
          <Text className="block text-gray-700 mt-3">Start with Program details — about 30 seconds.</Text>
        )}

        <RevealingLink
          text="Show details"
          expandedText="Hide details"
          className="mt-4"
          reveals={
            <Text className="block text-gray-700">
              Your family is responsible for any pending payments to your child care provider during this time.
            </Text>
          }
        />
      </div>
    );
  }

  if (state === STATES.NOT_STARTED) {
    return (
      <div className={wrapperClass}>
        {fmtSubmitted && <Text className="block text-gray-500 text-sm mb-1">Submitted {fmtSubmitted}</Text>}

        <Text className="block font-bold text-base mt-3">
          Your start date of eligibility is outside of the range for documents
        </Text>

        {!programAcknowledged ? (
          <Text className="block text-gray-700 mt-2">Start with Program details — about 30 seconds.</Text>
        ) : (
          <Text className="block text-gray-700 mt-2">
            No action needed right now. Please return no earlier than {fmtReturnDate} to begin reviewing requirements.
          </Text>
        )}
      </div>
    );
  }

  return null;
}
